import React, { useEffect, useState } from 'react';
import {
    Card,
    Typography,
    Space,
    Row,
    Col,
    Statistic,
    Progress,
    Descriptions,
    Button,
    Spin,
    Empty,
    Tag,
    Divider,
    Table,
    Badge,
    Tooltip,
    Modal,
    Form,
    Switch,
    InputNumber,
    Input,
    Select,
    TimePicker,
    DatePicker,
    message,
    ConfigProvider,
} from 'antd';
import {
    CalendarOutlined,
    EditOutlined,
    SaveOutlined,
    CloseOutlined,
    ClockCircleOutlined,
    UserOutlined,
    TeamOutlined,
    FileTextOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    BookOutlined,
    GlobalOutlined,
    ReloadOutlined,
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import 'dayjs/locale/lt';
import locale from 'antd/locale/lt_LT';

import { AppDispatch, CombinedStates } from '../../core/types';
import { getTrainingReport, createOrUpdateTrainingReport } from '../../core/center-accreditation/centerAccreditationApi';
import { CenterAccreditationTrainingReport as SchoolTrainingReportType } from '../../core/center-accreditation/types';
import { convertToBoolean } from '../../core/utils/helpers';

import './ScSeminars.scss';

// Set Lithuanian locale for dayjs
dayjs.locale('lt');

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const ScSeminars: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { trainingReport, isTrainingReportFetching } = useSelector((state: CombinedStates) => state.centerAccreditation);
    const { singleSchool } = useSelector((state: CombinedStates) => state.school);
    const { userData } = useSelector((state: CombinedStates) => state.user);

    const [form] = Form.useForm();
    const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(dayjs());

    useEffect(() => {
        dispatch(getTrainingReport({}));
    }, []);

    // Set form values when training report data is loaded
    useEffect(() => {
        if (trainingReport) {
            form.setFieldsValue({
                ...trainingReport,
                // Convert topics_offered string booleans
                topics_offered: {
                    pedagogine_sistema: convertToBoolean(trainingReport.topics_offered?.pedagogine_sistema),
                    ugdymo_tikslai: convertToBoolean(trainingReport.topics_offered?.ugdymo_tikslai),
                    ugdymo_turinys: convertToBoolean(trainingReport.topics_offered?.ugdymo_turinys),
                    ugdymo_metodai: convertToBoolean(trainingReport.topics_offered?.ugdymo_metodai),
                    mokytojo_vaidmuo: convertToBoolean(trainingReport.topics_offered?.mokytojo_vaidmuo),
                    mokinio_veikla: convertToBoolean(trainingReport.topics_offered?.mokinio_veikla),
                    mokymosi_motyvacija: convertToBoolean(trainingReport.topics_offered?.mokymosi_motyvacija),
                    vertinimas: convertToBoolean(trainingReport.topics_offered?.vertinimas),
                    mokykla: convertToBoolean(trainingReport.topics_offered?.mokykla),
                    organizacijos_kultura: convertToBoolean(trainingReport.topics_offered?.organizacijos_kultura),
                    mikroklimatas: convertToBoolean(trainingReport.topics_offered?.mikroklimatas),
                    socialine_aplinka: convertToBoolean(trainingReport.topics_offered?.socialine_aplinka),
                    vadyba: convertToBoolean(trainingReport.topics_offered?.vadyba),
                    svietimo_sistema: convertToBoolean(trainingReport.topics_offered?.svietimo_sistema),
                    svietimo_politika: convertToBoolean(trainingReport.topics_offered?.svietimo_politika),
                    svietimo_sistemos_struktura: convertToBoolean(trainingReport.topics_offered?.svietimo_sistemos_struktura),
                    mokytoju_rengimas_kvalifikacija: convertToBoolean(trainingReport.topics_offered?.mokytoju_rengimas_kvalifikacija),
                    pasiekimu_vertinimas_egz_tyrimai: convertToBoolean(trainingReport.topics_offered?.pasiekimu_vertinimas_egz_tyrimai),
                    other: trainingReport.topics_offered?.other || '',
                },
            });
        }
    }, [trainingReport, form]);

    const handleSave = async (values: any) => {
        const requestData = {
            ...values,
            school_id: singleSchool?.id,
            id: trainingReport?.id,
            date: selectedDate ? selectedDate.format('YYYY-MM-DD') : null,
        };

        await dispatch(createOrUpdateTrainingReport(requestData));
    };

    const handleRefresh = (date?: dayjs.Dayjs) => {
        const dateToUse = date || selectedDate;
        dispatch(getTrainingReport({ date: dateToUse ? dateToUse.format('YYYY-MM-DD') : null }));
    };

    const handleRefreshClick = () => {
        handleRefresh();
    };

    return (
        <ConfigProvider locale={locale}>
            <div className="school-seminars">
                <Card title="Mokymo ataskaitos duomenys" className="seminars-form-card" loading={isTrainingReportFetching || false}>
                    <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                            {trainingReport?.period_description ? (
                                <Text strong style={{ fontSize: '16px' }}>
                                    <CalendarOutlined style={{ marginRight: 8 }} />
                                    {trainingReport.period_description}
                                </Text>
                            ) : (
                                <Text strong style={{ fontSize: '16px' }}>
                                    Pasirinktam laikotarpiui duomenys nepateikti, norėdami pateikti duomenis užpildykite ir išsaugokite formą.
                                </Text>
                            )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <DatePicker
                                placeholder="Pasirinkite datą"
                                value={selectedDate}
                                onChange={(date) => {
                                    const newDate = date || dayjs();
                                    setSelectedDate(newDate);
                                    handleRefresh(newDate);
                                }}
                                format="YYYY-MM-DD"
                                style={{ width: 180 }}
                                allowClear={false}
                                disabledDate={(current) => current && current > dayjs().endOf('day')} />
                            <Tooltip title="Atnaujinti mokymo ataskaitos duomenis">
                                <Button
                                    icon={<ReloadOutlined />}
                                    onClick={handleRefreshClick}
                                    loading={!!isTrainingReportFetching}>
                                    Atnaujinti
                                </Button>
                            </Tooltip>
                        </div>
                    </div>
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleSave}
                        initialValues={{
                            topics_offered: {
                                pedagogine_sistema: false,
                                ugdymo_tikslai: false,
                                ugdymo_turinys: false,
                                ugdymo_metodai: false,
                                mokytojo_vaidmuo: false,
                                mokinio_veikla: false,
                                mokymosi_motyvacija: false,
                                vertinimas: false,
                                mokykla: false,
                                organizacijos_kultura: false,
                                mikroklimatas: false,
                                socialine_aplinka: false,
                                vadyba: false,
                                svietimo_sistema: false,
                                svietimo_politika: false,
                                svietimo_sistemos_struktura: false,
                                mokytoju_rengimas_kvalifikacija: false,
                                pasiekimu_vertinimas_egz_tyrimai: false,
                                other: '',
                            },
                            catalog_counts: {
                                seminar: 0,
                                course: 0,
                                lecture: 0,
                                other_1_label: '',
                                other_1_count: 0,
                                other_2_label: '',
                                other_2_count: 0,
                                educational_trips: 0,
                                continuous_training: 0,
                                total: 0,
                            },
                            annual_stats: {
                                seminar: { events: 0, hours: 0, participants: 0 },
                                course: { events: 0, hours: 0, participants: 0 },
                                lecture: { events: 0, hours: 0, participants: 0 },
                                other: { events: 0, hours: 0, participants: 0 },
                                educational_trips: { events: 0, hours: 0, participants: 0 },
                                continuous_training: { events: 0, hours: 0, participants: 0 },
                                other_1: { label: '', events: 0, hours: 0, participants: 0 },
                                other_2: { label: '', events: 0, hours: 0, participants: 0 },
                                total: { events: 0, hours: 0, participants: 0 },
                            },
                            location_breakdown: {
                                seminar: { in_house: 0, at_clients: 0 },
                                course: { in_house: 0, at_clients: 0 },
                                lecture: { in_house: 0, at_clients: 0 },
                                other: { in_house: 0, at_clients: 0 },
                            },
                            target_group_stats: {
                                ikimokyklinis: { events: 0, hours: 0, participants: 0 },
                                priesmokyklinis: { events: 0, hours: 0, participants: 0 },
                                pradinis: { events: 0, hours: 0, participants: 0 },
                                pagr_viduriniai: { events: 0, hours: 0, participants: 0 },
                                vadovai: { events: 0, hours: 0, participants: 0 },
                                spec_pedagogai: { events: 0, hours: 0, participants: 0 },
                                profesinis: { events: 0, hours: 0, participants: 0 },
                                neformalus: { events: 0, hours: 0, participants: 0 },
                                bendruomene: { events: 0, hours: 0, participants: 0 },
                                savivaldybiu_darbuotojai: { events: 0, hours: 0, participants: 0 },
                                other: { label: '', events: 0, hours: 0, participants: 0 },
                            },
                            notes: '',
                        }}>

                        {/* 3.1 Kokių sričių seminarų, kursų, paskaitų ir kitų renginių galite pasiūlyti klausytojams */}
                        <Card title="3.1 Kokių sričių seminarų, kursų, paskaitų ir kitų renginių galite pasiūlyti klausytojams?" style={{ marginBottom: 16 }}>
                            <Row gutter={[24, 16]}>
                                <Col span={8}>
                                    <Title level={5}>3.1.1 Pedagoginė sistema</Title>
                                    <Form.Item name={['topics_offered', 'pedagogine_sistema']} label="Pedagoginė sistema" valuePropName="checked">
                                        <Switch />
                                    </Form.Item>
                                    <Form.Item name={['topics_offered', 'ugdymo_tikslai']} label="3.1.1.1 Ugdymo tikslai" valuePropName="checked">
                                        <Switch />
                                    </Form.Item>
                                    <Form.Item name={['topics_offered', 'ugdymo_turinys']} label="3.1.1.2 Ugdymo turinys" valuePropName="checked">
                                        <Switch />
                                    </Form.Item>
                                    <Form.Item name={['topics_offered', 'ugdymo_metodai']} label="3.1.1.3 Ugdymo metodai" valuePropName="checked">
                                        <Switch />
                                    </Form.Item>
                                    <Form.Item name={['topics_offered', 'mokytojo_vaidmuo']} label="3.1.1.4 Mokytojo vaidmuo" valuePropName="checked">
                                        <Switch />
                                    </Form.Item>
                                    <Form.Item name={['topics_offered', 'mokinio_veikla']} label="3.1.1.5 Mokinio veikla" valuePropName="checked">
                                        <Switch />
                                    </Form.Item>
                                    <Form.Item name={['topics_offered', 'mokymosi_motyvacija']} label="3.1.1.6 Mokymosi motyvacija" valuePropName="checked">
                                        <Switch />
                                    </Form.Item>
                                    <Form.Item name={['topics_offered', 'vertinimas']} label="3.1.1.7 Vertinimas" valuePropName="checked">
                                        <Switch />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Title level={5}>3.1.2 Mokykla</Title>
                                    <Form.Item name={['topics_offered', 'mokykla']} label="Mokykla" valuePropName="checked">
                                        <Switch />
                                    </Form.Item>
                                    <Form.Item name={['topics_offered', 'organizacijos_kultura']} label="3.1.2.1 Organizacijos kultūra" valuePropName="checked">
                                        <Switch />
                                    </Form.Item>
                                    <Form.Item name={['topics_offered', 'mikroklimatas']} label="3.1.2.2 Mikroklimatas" valuePropName="checked">
                                        <Switch />
                                    </Form.Item>
                                    <Form.Item name={['topics_offered', 'socialine_aplinka']} label="3.1.2.3 Socialinė aplinka" valuePropName="checked">
                                        <Switch />
                                    </Form.Item>
                                    <Form.Item name={['topics_offered', 'vadyba']} label="3.1.2.4 Vadyba" valuePropName="checked">
                                        <Switch />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Title level={5}>3.1.3 Švietimo sistema</Title>
                                    <Form.Item name={['topics_offered', 'svietimo_sistema']} label="Švietimo sistema" valuePropName="checked">
                                        <Switch />
                                    </Form.Item>
                                    <Form.Item name={['topics_offered', 'svietimo_politika']} label="3.1.3.1 Švietimo politika" valuePropName="checked">
                                        <Switch />
                                    </Form.Item>
                                    <Form.Item name={['topics_offered', 'svietimo_sistemos_struktura']} label="3.1.3.2 Švietimo sistemos struktūra" valuePropName="checked">
                                        <Switch />
                                    </Form.Item>
                                    <Form.Item name={['topics_offered', 'mokytoju_rengimas_kvalifikacija']} label="3.1.3.4 Mokytojų rengimas ir kvalifikacijos tobulinimas" valuePropName="checked">
                                        <Switch />
                                    </Form.Item>
                                    <Form.Item name={['topics_offered', 'pasiekimu_vertinimas_egz_tyrimai']} label="3.1.3.5 Mokymosi pasiekimų vertinimas (egzaminai, tarptautiniai tyrimai)" valuePropName="checked">
                                        <Switch />
                                    </Form.Item>
                                </Col>
                                <Col span={24}>
                                    <Form.Item name={['topics_offered', 'other']} label="3.1.4 Kita (įrašykite)">
                                        <Input />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Card>

                        {/* 3.2 Kokiu skaičiumi skirtingų pavadinimų seminarų, kursų, paskaitų ir kitų renginių disponuoja institucija */}
                        <Card title="3.2 Kokiu skaičiumi skirtingų pavadinimų seminarų, kursų, paskaitų ir kitų renginių disponuoja institucija? (įrašykite skaičių)" style={{ marginBottom: 16 }}>
                            <Row gutter={16}>
                                <Col span={6}>
                                    <Form.Item name={['catalog_counts', 'seminar']} label="3.2.1 Seminarai">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={6}>
                                    <Form.Item name={['catalog_counts', 'course']} label="3.2.2 Kursai">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={6}>
                                    <Form.Item name={['catalog_counts', 'lecture']} label="3.2.3 Paskaitos">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row gutter={16}>
                                <Col span={24}>
                                    <Divider />
                                    <Title level={5}>3.2.4 Kiti renginiai, iš jų:</Title>
                                </Col>
                                <Col span={6}>
                                    <Form.Item name={['catalog_counts', 'educational_trips']} label="3.2.4.1 Edukacinės išvykos">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={6}>
                                    <Form.Item name={['catalog_counts', 'continuous_training']} label="3.2.4.2 Tęstiniai mokymai">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={6}>
                                    <Form.Item name={['catalog_counts', 'other_1_label']} label="3.2.4.3 Kiti renginiai (pavadinimas)">
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col span={6}>
                                    <Form.Item name={['catalog_counts', 'other_1_count']} label="3.2.4.3 Skaičius">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={6}>
                                    <Form.Item name={['catalog_counts', 'other_2_label']} label="3.2.4.4 Kiti renginiai (pavadinimas)">
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col span={6}>
                                    <Form.Item name={['catalog_counts', 'other_2_count']} label="3.2.4.4 Skaičius">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                            </Row>

                            {/* Display catalog_counts_sum */}

                            <Row style={{ marginTop: 16 }}>
                                <Col span={24}>
                                    <div className="counts-sum">
                                        <span className="counts-sum-label">
                                            3.2.5 Iš viso:
                                        </span>
                                        <span className="counts-sum-value">
                                            {trainingReport?.catalog_counts_sum}
                                        </span>
                                    </div>
                                </Col>
                            </Row>
                        </Card>

                        {/* 3.3 Kiek seminarų, kursų, paskaitų ir kitų renginių mokytojams institucija organizavo per ataskaitinius metus */}
                        <Card title="3.3 Kiek seminarų, kursų, paskaitų ir kitų renginių mokytojams institucija organizavo per ataskaitinius metus? (įrašykite skaičių)" style={{ marginBottom: 16 }}>
                            <Row gutter={[24, 16]}>
                                <Col span={8}>
                                    <Title level={5}>3.3.1 Seminarai</Title>
                                    <Form.Item name={['annual_stats', 'seminar', 'events']} label="Skaičius">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                    <Form.Item name={['annual_stats', 'seminar', 'hours']} label="Valandų skaičius">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                    <Form.Item name={['annual_stats', 'seminar', 'participants']} label="Dalyvių skaičius">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Title level={5}>3.3.2 Kursai</Title>
                                    <Form.Item name={['annual_stats', 'course', 'events']} label="Skaičius">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                    <Form.Item name={['annual_stats', 'course', 'hours']} label="Valandų skaičius">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                    <Form.Item name={['annual_stats', 'course', 'participants']} label="Dalyvių skaičius">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Title level={5}>3.3.3 Paskaitos</Title>
                                    <Form.Item name={['annual_stats', 'lecture', 'events']} label="Skaičius">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                    <Form.Item name={['annual_stats', 'lecture', 'hours']} label="Valandų skaičius">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                    <Form.Item name={['annual_stats', 'lecture', 'participants']} label="Dalyvių skaičius">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Title level={5}>3.3.4.1 Edukacinės išvykos</Title>
                                    <Form.Item name={['annual_stats', 'educational_trips', 'events']} label="Skaičius">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                    <Form.Item name={['annual_stats', 'educational_trips', 'hours']} label="Valandų skaičius">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                    <Form.Item name={['annual_stats', 'educational_trips', 'participants']} label="Dalyvių skaičius">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Title level={5}>3.3.4.2 Tęstiniai mokymai</Title>
                                    <Form.Item name={['annual_stats', 'continuous_training', 'events']} label="Skaičius">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                    <Form.Item name={['annual_stats', 'continuous_training', 'hours']} label="Valandų skaičius">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                    <Form.Item name={['annual_stats', 'continuous_training', 'participants']} label="Dalyvių skaičius">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Title level={5}>3.3.4.3 Kiti renginiai</Title>
                                    <Form.Item name={['annual_stats', 'other_1', 'label']} label="Pavadinimas">
                                        <Input />
                                    </Form.Item>
                                    <Form.Item name={['annual_stats', 'other_1', 'events']} label="Skaičius">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                    <Form.Item name={['annual_stats', 'other_1', 'hours']} label="Valandų skaičius">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                    <Form.Item name={['annual_stats', 'other_1', 'participants']} label="Dalyvių skaičius">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Title level={5}>3.3.4.4 Kiti renginiai</Title>
                                    <Form.Item name={['annual_stats', 'other_2', 'label']} label="Pavadinimas">
                                        <Input />
                                    </Form.Item>
                                    <Form.Item name={['annual_stats', 'other_2', 'events']} label="Skaičius">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                    <Form.Item name={['annual_stats', 'other_2', 'hours']} label="Valandų skaičius">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                    <Form.Item name={['annual_stats', 'other_2', 'participants']} label="Dalyvių skaičius">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                            </Row>

                            {/* Display annual stats sums */}
                            <Row style={{ marginTop: 16 }}>
                                <Col span={24}>
                                    <div className="counts-sum">
                                        <span className="counts-sum-label">
                                            3.3.4 Iš viso:
                                        </span>
                                        <div style={{ display: 'flex', gap: '24px', marginLeft: '7px' }}>
                                            <span className="counts-sum-value">
                                                Renginiai: {trainingReport?.annual_stats_events_sum}
                                            </span>
                                            <span className="counts-sum-value">
                                                Valandos: {trainingReport?.annual_stats_hours_sum}
                                            </span>
                                            <span className="counts-sum-value">
                                                Dalyviai: {trainingReport?.annual_stats_participants_sum}
                                            </span>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </Card>

                        {/* 3.4 Kiek seminarų, kursų, paskaitų vyko institucijos patalpose ir kiek klientų darbo vietose */}
                        <Card title="3.4 Kiek seminarų, kursų, paskaitų vyko institucijos patalpose ir kiek klientų darbo vietose? (įrašykite skaičių)" style={{ marginBottom: 16 }}>
                            <Row gutter={[24, 16]}>
                                <Col span={6}>
                                    <Title level={5}>3.4.1 Seminarai</Title>
                                    <Form.Item name={['location_breakdown', 'seminar', 'in_house']} label="Institucijos patalpose">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                    <Form.Item name={['location_breakdown', 'seminar', 'at_clients']} label="Klientų darbo vietose">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={6}>
                                    <Title level={5}>3.4.2 Kursai</Title>
                                    <Form.Item name={['location_breakdown', 'course', 'in_house']} label="Institucijos patalpose">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                    <Form.Item name={['location_breakdown', 'course', 'at_clients']} label="Klientų darbo vietose">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={6}>
                                    <Title level={5}>3.4.3 Paskaitos</Title>
                                    <Form.Item name={['location_breakdown', 'lecture', 'in_house']} label="Institucijos patalpose">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                    <Form.Item name={['location_breakdown', 'lecture', 'at_clients']} label="Klientų darbo vietose">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={6}>
                                    <Title level={5}>3.4.4 Kiti renginiai</Title>
                                    <Form.Item name={['location_breakdown', 'other', 'in_house']} label="Institucijos patalpose">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                    <Form.Item name={['location_breakdown', 'other', 'at_clients']} label="Klientų darbo vietose">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Card>

                        {/* 3.5 Kokios seminarų dalyvių tikslinės grupės */}
                        <Card title="3.5 Kokios seminarų dalyvių tikslinės grupės?" style={{ marginBottom: 16 }}>
                            <Row gutter={[24, 16]}>
                                <Col span={8}>
                                    <Title level={5}>3.5.1 Ikimokyklinio ugdymo mokytojai</Title>
                                    <Form.Item name={['target_group_stats', 'ikimokyklinis', 'events']} label="Seminarų skaičius">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                    <Form.Item name={['target_group_stats', 'ikimokyklinis', 'hours']} label="Valandų skaičius">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                    <Form.Item name={['target_group_stats', 'ikimokyklinis', 'participants']} label="Dalyvių skaičius">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Title level={5}>3.5.2 Priešmokyklinio ugdymo mokytojai</Title>
                                    <Form.Item name={['target_group_stats', 'priesmokyklinis', 'events']} label="Seminarų skaičius">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                    <Form.Item name={['target_group_stats', 'priesmokyklinis', 'hours']} label="Valandų skaičius">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                    <Form.Item name={['target_group_stats', 'priesmokyklinis', 'participants']} label="Dalyvių skaičius">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Title level={5}>3.5.3 Pradinio ugdymo mokytojai</Title>
                                    <Form.Item name={['target_group_stats', 'pradinis', 'events']} label="Seminarų skaičius">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                    <Form.Item name={['target_group_stats', 'pradinis', 'hours']} label="Valandų skaičius">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                    <Form.Item name={['target_group_stats', 'pradinis', 'participants']} label="Dalyvių skaičius">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Title level={5}>3.5.4 Pagrindinių ir vidurinių mokyklų dalykų mokytojai</Title>
                                    <Form.Item name={['target_group_stats', 'pagr_viduriniai', 'events']} label="Seminarų skaičius">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                    <Form.Item name={['target_group_stats', 'pagr_viduriniai', 'hours']} label="Valandų skaičius">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                    <Form.Item name={['target_group_stats', 'pagr_viduriniai', 'participants']} label="Dalyvių skaičius">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Title level={5}>3.5.5 Mokyklų vadovai, jų pavaduotojai ugdymui, ugdymą organizuojančių skyrių vedėjai</Title>
                                    <Form.Item name={['target_group_stats', 'vadovai', 'events']} label="Seminarų skaičius">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                    <Form.Item name={['target_group_stats', 'vadovai', 'hours']} label="Valandų skaičius">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                    <Form.Item name={['target_group_stats', 'vadovai', 'participants']} label="Dalyvių skaičius">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Title level={5}>3.5.6 Specialiojo ugdymo pedagogai</Title>
                                    <Form.Item name={['target_group_stats', 'spec_pedagogai', 'events']} label="Seminarų skaičius">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                    <Form.Item name={['target_group_stats', 'spec_pedagogai', 'hours']} label="Valandų skaičius">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                    <Form.Item name={['target_group_stats', 'spec_pedagogai', 'participants']} label="Dalyvių skaičius">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Title level={5}>3.5.7 Profesinio rengimo mokytojai</Title>
                                    <Form.Item name={['target_group_stats', 'profesinis', 'events']} label="Seminarų skaičius">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                    <Form.Item name={['target_group_stats', 'profesinis', 'hours']} label="Valandų skaičius">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                    <Form.Item name={['target_group_stats', 'profesinis', 'participants']} label="Dalyvių skaičius">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Title level={5}>3.5.8 Neformaliojo vaikų švietimo mokytojai</Title>
                                    <Form.Item name={['target_group_stats', 'neformalus', 'events']} label="Seminarų skaičius">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                    <Form.Item name={['target_group_stats', 'neformalus', 'hours']} label="Valandų skaičius">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                    <Form.Item name={['target_group_stats', 'neformalus', 'participants']} label="Dalyvių skaičius">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Title level={5}>3.5.9 Mokyklų bendruomenės, komandos</Title>
                                    <Form.Item name={['target_group_stats', 'bendruomene', 'events']} label="Seminarų skaičius">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                    <Form.Item name={['target_group_stats', 'bendruomene', 'hours']} label="Valandų skaičius">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                    <Form.Item name={['target_group_stats', 'bendruomene', 'participants']} label="Dalyvių skaičius">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Title level={5}>3.5.10 Apskričių viršininkų, savivaldybių administracijų švietimo padalinių darbuotojai</Title>
                                    <Form.Item name={['target_group_stats', 'savivaldybiu_darbuotojai', 'events']} label="Seminarų skaičius">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                    <Form.Item name={['target_group_stats', 'savivaldybiu_darbuotojai', 'hours']} label="Valandų skaičius">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                    <Form.Item name={['target_group_stats', 'savivaldybiu_darbuotojai', 'participants']} label="Dalyvių skaičius">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Title level={5}>3.5.11 Kita</Title>
                                    <Form.Item name={['target_group_stats', 'other', 'label']} label="Pavadinimas">
                                        <Input />
                                    </Form.Item>
                                    <Form.Item name={['target_group_stats', 'other', 'events']} label="Seminarų skaičius">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                    <Form.Item name={['target_group_stats', 'other', 'hours']} label="Valandų skaičius">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                    <Form.Item name={['target_group_stats', 'other', 'participants']} label="Dalyvių skaičius">
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Card>

                        {/* Papildomi komentarai */}
                        <Card title="Papildomi komentarai" style={{ marginBottom: 16 }}>
                            <Form.Item name="notes" label="Papildomi komentarai apie mokymo veiklą ir pasiekimus">
                                <TextArea rows={4} />
                            </Form.Item>
                        </Card>

                        {/* Form Actions */}
                        <div style={{ textAlign: 'right', marginTop: 24 }}>
                            <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>
                                Išsaugoti
                            </Button>
                        </div>
                    </Form>
                </Card>
            </div>
        </ConfigProvider>
    );
};

export default ScSeminars;
